import React, { useCallback, useMemo, useState } from "react";

import AddSubscriptionModal from "./components/AddSubscriptionModal";
import EditSubscriptionModal from "./components/EditSubscriptionModal";

import SubscriptionsHeader from "./components/SubscriptionsHeader";
import SubscriptionsViewTabs from "./components/SubscriptionsViewTabs";
import SubscriptionOverview from "./components/SubscriptionOverview";
import SubscriptionRenewalKanban from "./components/SubscriptionRenewalKanban";
import SubscriptionForecastView from "./components/SubscriptionForecastView";
import SubscriptionsListSection from "./components/SubscriptionsListSection";

import DeleteSubscriptionModal from "./ui/DeleteSubscriptionModal";
import SubscriptionsErrorBanner from "./ui/SubscriptionsErrorBanner";

import useSubscriptions from "./hooks/useSubscriptions";
import useSubscriptionDashboard from "./hooks/useSubscriptionDashboard";
import useSubscriptionFilters from "./hooks/useSubscriptionFilters";

export default function SubscriptionsPage() {
  const [activeView, setActiveView] = useState("overview");

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [editingSubscription, setEditingSubscription] = useState(null);

  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);

  const [deleteError, setDeleteError] = useState("");

  const refreshDashboardAlerts = useCallback(() => {
    window.dispatchEvent(new Event("dashboard-alerts:refresh"));
  }, []);

  /* ==========================================================================
   * ABONNEMENTS / PRESTATIONS
   * ========================================================================== */

  const {
    subscriptions,

    isLoading: isSubscriptionsLoading,

    isRefreshing: isSubscriptionsRefreshing,

    error: subscriptionsError,

    deletingId,

    reloadSubscriptions,
    removeSubscription,
    upsertSubscription,
  } = useSubscriptions();

  /* ==========================================================================
   * DASHBOARD COMMERCIAL
   * ========================================================================== */

  const {
    dashboard,

    isLoading: isDashboardLoading,

    isRefreshing: isDashboardRefreshing,

    error: dashboardError,

    reloadDashboard,
  } = useSubscriptionDashboard({
    initialMonths: 12,
    initialRenewalDays: 30,
  });

  /* ==========================================================================
   * FILTRES TABLEAU
   * ========================================================================== */

  const filters = useSubscriptionFilters(subscriptions, {
    initialPageSize: 15,
  });

  const isRefreshing = isSubscriptionsRefreshing || isDashboardRefreshing;

  /* ==========================================================================
   * COMPTEURS ONGLETS
   * ========================================================================== */

  const viewCounts = useMemo(() => {
    const pipeline = dashboard?.renewalPipeline || {};

    const renewalCount =
      (pipeline.toPrepare?.length || 0) +
      (pipeline.quoteSent?.length || 0) +
      (pipeline.followUp?.length || 0) +
      (pipeline.accepted?.length || 0);

    return {
      overview: dashboard?.summary?.urgentActionsCount || 0,

      renewals: renewalCount,

      forecast:
        dashboard?.forecast?.months?.reduce(
          (sum, month) => sum + Number(month.renewal_count || 0),
          0,
        ) || 0,

      contracts: subscriptions.length,
    };
  }, [dashboard, subscriptions.length]);

  /* ==========================================================================
   * REFRESH
   * ========================================================================== */

  const refreshEverything = useCallback(
    async ({ silent = true } = {}) => {
      await Promise.allSettled([
        reloadSubscriptions({
          silent,
        }),

        reloadDashboard({
          silent,
        }),
      ]);
    },
    [reloadSubscriptions, reloadDashboard],
  );

  /* ==========================================================================
   * CREATE
   * ========================================================================== */

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleSubscriptionCreated = async (createdSubscription) => {
    if (createdSubscription?.id) {
      upsertSubscription(createdSubscription);
    }

    setIsAddModalOpen(false);

    await refreshEverything({
      silent: true,
    });
    refreshDashboardAlerts();
  };

  /* ==========================================================================
   * UPDATE
   * ========================================================================== */

  const handleOpenEditModal = (subscription) => {
    setEditingSubscription(subscription);
  };

  const handleCloseEditModal = () => {
    setEditingSubscription(null);
  };

  const handleSubscriptionUpdated = async (updatedSubscription) => {
    if (updatedSubscription?.id) {
      upsertSubscription(updatedSubscription);
    }

    setEditingSubscription(null);

    await refreshEverything({
      silent: true,
    });
    refreshDashboardAlerts();
  };

  /* ==========================================================================
   * DELETE
   * ========================================================================== */

  const handleRequestDelete = (subscription) => {
    setDeleteError("");

    setSubscriptionToDelete(subscription);
  };

  const handleCancelDelete = () => {
    if (deletingId) return;

    setDeleteError("");

    setSubscriptionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!subscriptionToDelete?.id) {
      return;
    }

    setDeleteError("");

    try {
      await removeSubscription(subscriptionToDelete.id);

      setSubscriptionToDelete(null);

      await reloadDashboard({
        silent: true,
      });
      refreshDashboardAlerts();
    } catch (error) {
      setDeleteError(error?.message || "Impossible de supprimer cet élément.");
    }
  };

  /* ==========================================================================
   * RENDER
   * ========================================================================== */

  return (
    <main className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="mx-auto max-w-[1750px] space-y-6 px-3 py-5 sm:px-5 lg:px-7 lg:py-7">
        <SubscriptionsHeader
          onRefresh={() =>
            refreshEverything({
              silent: true,
            })
          }
          onAdd={handleOpenAddModal}
          isRefreshing={isRefreshing}
        />

        <SubscriptionsViewTabs
          activeView={activeView}
          onChange={setActiveView}
          counts={viewCounts}
        />

        {subscriptionsError && (
          <SubscriptionsErrorBanner
            title="La liste n’a pas pu être chargée"
            message={subscriptionsError}
            onRetry={() =>
              reloadSubscriptions({
                silent: false,
              })
            }
            isRetrying={isSubscriptionsRefreshing}
          />
        )}

        {dashboardError && (
          <SubscriptionsErrorBanner
            title="Les indicateurs ne sont pas disponibles"
            message={dashboardError}
            onRetry={() =>
              reloadDashboard({
                silent: false,
              })
            }
            isRetrying={isDashboardRefreshing}
          />
        )}

        {activeView === "overview" && (
          <SubscriptionOverview
            dashboard={dashboard}
            isLoading={isDashboardLoading}
            onEdit={handleOpenEditModal}
            onOpenRenewals={() => setActiveView("renewals")}
            onOpenForecast={() => setActiveView("forecast")}
          />
        )}

        {activeView === "renewals" && (
          <SubscriptionRenewalKanban
            pipeline={dashboard?.renewalPipeline}
            isLoading={isDashboardLoading}
            onEdit={handleOpenEditModal}
          />
        )}

        {activeView === "forecast" && (
          <SubscriptionForecastView
            forecast={dashboard?.forecast}
            timeline={dashboard?.renewalTimeline}
            isLoading={isDashboardLoading}
            onEdit={handleOpenEditModal}
          />
        )}

        {activeView === "contracts" && (
          <SubscriptionsListSection
            filters={filters}
            isLoading={isSubscriptionsLoading}
            deletingId={deletingId}
            onEdit={handleOpenEditModal}
            onDelete={handleRequestDelete}
          />
        )}
      </div>

      <AddSubscriptionModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        onSuccess={handleSubscriptionCreated}
      />

      <EditSubscriptionModal
        isOpen={Boolean(editingSubscription)}
        subscription={editingSubscription}
        subscriptionId={editingSubscription?.id || null}
        onClose={handleCloseEditModal}
        onSuccess={handleSubscriptionUpdated}
      />

      <DeleteSubscriptionModal
        subscription={subscriptionToDelete}
        isDeleting={Boolean(deletingId)}
        error={deleteError}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </main>
  );
}
